import { useCallback, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useConversationStore } from '@/stores/conversationStore'
import { useAuthStore } from '@/stores/authStore'
import { useSSE, useStreamingContent } from './useSSE'
import { api } from '@/lib/api'
import { streamAAI } from '@/lib/aaiClient'
import type { Answer, AAISuggestion } from '@/types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Hook for managing conversation flow with SSE streaming
 */
export function useConversation(projectId: string) {
  const { token } = useAuthStore()
  const store = useConversationStore()
  const streaming = useStreamingContent()
  
  const headers = {
    'Authorization': `Bearer ${token}`,
  }
  
  const sse = useSSE({
    headers,
    onMessage: (data) => {
      switch (data.type) {
        // Cleaner AI events
        case 'cleaning_start':
          store.setState('CLEANING')
          store.setStreaming(true)
          streaming.reset()
          break
          
        case 'cleaning_chunk':
          // Cleaner output is intentionally NOT streamed to the UI per
          // user preference — we keep a generic "Cleaning in progress…"
          // indicator and reveal the result only on `cleaning_complete`.
          break
          
        case 'cleaning_complete':
          store.setCleanedSituation(data.cleaned)
          store.setDiff(data.diff || [])
          store.setCleanerFlags({
            vaguenessIssues: data.vaguenessIssues || [],
            requiresClarification: !!data.requiresClarification,
          })
          store.setState('CONFIRM_CLEAN')
          store.setStreaming(false)
          streaming.complete()
          break
        
        // QAI events
        case 'qai_start':
          store.setState('QUESTIONING')
          store.setStreaming(true)
          break
          
        case 'branch_created':
          store.addBranch(data.branch)
          if (!store.activeBranchId) {
            store.setActiveBranch(data.branch._id)
          }
          // Round model v2: track new parallels spawned mid-conversation
          // so the workspace can announce them.
          if (data.branch?.spawnedAtRound && data.branch.spawnedAtRound > 1) {
            store.bumpNewBranchesThisRound()
            const label =
              data.branch.userRenamedName ||
              data.branch.thematicName ||
              data.branch.name ||
              'New branch'
            toast.success(`New branch spawned: ${label}`, { duration: 4500 })
          }
          break

        case 'questions':
          store.setQuestionsForBranch(data.branchId, data.questions)
          store.setState('ANSWERING')
          store.setStreaming(false)
          break

        case 'qai_complete':
          store.setStreaming(false)
          if (data.depthWarning) {
            toast(String(data.depthWarning), { icon: 'warning', duration: 6000 })
          }
          break

        // Round model v2 events
        case 'round_start':
          store.setRoundSubmitting(true)
          store.resetNewBranchesThisRound()
          store.setAutoGenerateModalShown(false)
          break

        case 'depth_warning':
          if (data.message) {
            toast(String(data.message), { icon: 'warning', duration: 6000 })
          }
          break
        
        // AAI events are handled out-of-band by the standalone AAI
        // streaming client (see `lib/aaiClient.ts` and `requestAAIHelp`
        // below). They are intentionally not wired into the shared SSE
        // hook so a new round/QAI/OAI request cannot cancel an
        // in-flight Assist, and so multiple Assist requests can run in
        // parallel — one connection per question.

        // OAI events. Multi-card synthesis: we no longer flip the
        // global state to GENERATING (that would block the chat panel
        // for branches the user might want to keep answering in).
        // The synthesis row's `status='generating'` drives the
        // streaming UI inside the card itself.
        case 'oai_start':
        case 'analyzing':
        case 'analyzing_tree':
          store.setStreaming(true)
          streaming.reset()
          break
          
        case 'generating_chunk':
          streaming.append(data.content || '')
          store.setStreamContent(streaming.content + (data.content || ''))
          break
          
        case 'problem_complete':
          store.setProblemOutput(data.output)
          // Multi-card synthesis: the synthesis card itself owns the
          // "review" UI now (gated by `activeSynthesisId`), so we do
          // NOT force the global state to REVIEWING anymore. Drop back
          // to ANSWERING so the chat panel is still available when
          // the user clicks a branch in the sidebar.
          store.setState('ANSWERING')
          store.setStreaming(false)
          streaming.complete()
          break
        
        // Revision events
        case 'oai_revision_start':
          store.setStreaming(true)
          streaming.reset()
          break
          
        case 'revision_chunk':
          streaming.append(data.content || '')
          break
          
        case 'revision_complete':
          store.setProblemOutput(data.output)
          // Same reasoning as problem_complete — synthesis card owns
          // the rendering, not the global state machine.
          store.setStreaming(false)
          break
        
        // Branch events
        case 'branch_terminated':
          store.updateBranch(data.branchId, { 
            status: 'terminated_natural',
            terminationReason: data.reason,
          })
          break
          
        case 'round_complete':
          store.setRoundSubmitting(false)
          if (typeof data.round === 'number') {
            store.setCurrentRound(data.round)
          } else {
            store.advanceRound()
          }
          // Drop any per-question answers we held in memory; the next
          // round's questions will start with empty answers.
          for (const b of store.getActiveBranches()) {
            store.clearAnswersForBranch(b._id)
          }
          break
        
        // Error events
        case 'error':
          store.setError(data.message, data.recoverable)
          store.setStreaming(false)
          store.setRoundSubmitting(false)
          break
      }
    },
    onError: (error) => {
      store.setError(error.message, true)
      store.setStreaming(false)
    },
    onComplete: () => {
      store.setStreaming(false)
    },
  })
  
  // ---- Submit initial situation ----
  const submitSituation = useCallback(async (situation: string) => {
    store.setLastAction({ type: 'submitSituation', args: { situation } })
    store.setRawSituation(situation)
    await sse.connect(`${API_URL}/api/projects/${projectId}/situation`, { situation })
  }, [projectId, sse, store])
  
  // ---- Confirm cleaned situation ----
  const confirmSituation = useCallback(async (useOriginal: boolean = false) => {
    store.setLastAction({ type: 'confirmSituation', args: { useOriginal } })
    await sse.connect(`${API_URL}/api/projects/${projectId}/situation/confirm`, { useOriginal })
  }, [projectId, sse, store])
  
  // ---- Submit answers ----
  const submitAnswers = useCallback(async (branchId: string, answers: Answer[]) => {
    store.setLastAction({ type: 'submitAnswers', args: { branchId, answers: answers as unknown as Record<string, unknown>[] } })
    for (const answer of answers) {
      store.setAnswerForQuestion(branchId, answer.questionId, answer)
    }
    await sse.connect(
      `${API_URL}/api/projects/${projectId}/branches/${branchId}/answers`,
      { answers }
    )
  }, [projectId, sse, store])
  
  // ---- Request AAI assistance (parallel-capable) ----
  // Each click on "Assist (AAI)" opens its own SSE connection via the
  // standalone `streamAAI` client and writes into a per-question slot
  // in the conversation store. Clicking another question's button does
  // NOT cancel a previous request — they run independently. Clicking
  // the *same* question twice does cancel that question's prior
  // request (so we don't leak controllers).
  const aaiAbortMap = useRef<Map<string, AbortController>>(new Map())

  const requestAAIHelp = useCallback(
    async (questionId: string, branchId: string, context?: string) => {
      if (!questionId || !branchId) {
        console.error('requestAAIHelp: missing questionId or branchId', {
          questionId,
          branchId,
        })
        return
      }

      // Cancel any prior in-flight request for THIS question only.
      const existing = aaiAbortMap.current.get(questionId)
      if (existing) {
        existing.abort()
      }
      const controller = new AbortController()
      aaiAbortMap.current.set(questionId, controller)

      store.setLastAction({
        type: 'requestAAIHelp',
        args: { questionId, branchId, context },
      })

      // Initialise the slot — this is what the QuestionBlock subscribes
      // to in order to render the "thinking…" / suggestion UI.
      store.aaiSlotPatch(questionId, {
        isActive: true,
        webSearchInProgress: false,
        webSearchResults: undefined,
        streamingContent: '',
        suggestion: null,
        error: undefined,
        startedAt: Date.now(),
      })

      try {
        await streamAAI({
          projectId,
          questionId,
          branchId,
          context,
          forceWebSearch: false,
          token,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case 'aai_start':
                store.aaiSlotPatch(questionId, { isActive: true })
                break
              case 'web_search_start':
                store.aaiSlotPatch(questionId, { webSearchInProgress: true })
                break
              case 'web_search_results':
                store.aaiSlotPatch(questionId, {
                  webSearchInProgress: false,
                  webSearchResults:
                    (event.results as AAISuggestion['sources']) ?? undefined,
                })
                break
              case 'suggestion_chunk':
                store.aaiSlotAppendStream(
                  questionId,
                  String(event.content ?? ''),
                )
                break
              case 'suggestion_complete': {
                const suggestion: AAISuggestion = {
                  suggestion: String(event.suggestion ?? ''),
                  confidence: Number(event.confidence ?? 0),
                  webSearchUsed: !!event.webSearchUsed,
                  sources: event.sources as AAISuggestion['sources'],
                  keyFacts: event.keyFacts as string[] | undefined,
                  caveats: event.caveats as string[] | undefined,
                  alternativeAngles: event.alternativeAngles as
                    | string[]
                    | undefined,
                }
                store.aaiSlotPatch(questionId, {
                  isActive: false,
                  webSearchInProgress: false,
                  suggestion,
                })
                break
              }
              case 'error':
                store.aaiSlotPatch(questionId, {
                  isActive: false,
                  webSearchInProgress: false,
                  error: String(event.message ?? 'AAI failed'),
                })
                toast.error(String(event.message ?? 'AAI failed'))
                break
            }
          },
        })
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        store.aaiSlotPatch(questionId, {
          isActive: false,
          webSearchInProgress: false,
          error: msg,
        })
        toast.error(`AAI failed: ${msg}`)
      } finally {
        // Only clear the controller if it's still ours (a re-click for
        // the same question would have replaced it).
        if (aaiAbortMap.current.get(questionId) === controller) {
          aaiAbortMap.current.delete(questionId)
        }
      }
    },
    [projectId, token, store],
  )

  // ---- Cancel an in-flight AAI request (per question) ----
  const cancelAAI = useCallback((questionId: string) => {
    const controller = aaiAbortMap.current.get(questionId)
    if (controller) {
      controller.abort()
      aaiAbortMap.current.delete(questionId)
    }
  }, [])

  // ---- Dismiss / accept the suggestion for a single question ----
  const dismissAAI = useCallback(
    (questionId: string) => {
      cancelAAI(questionId)
      store.aaiSlotClear(questionId)
    },
    [cancelAAI, store],
  )

  // Cancel every in-flight AAI request when the hook unmounts (e.g.
  // user navigates away from the workspace).
  useEffect(() => {
    return () => {
      for (const c of aaiAbortMap.current.values()) c.abort()
      aaiAbortMap.current.clear()
    }
  }, [])
  
  // ---- Generate problem statement ----
  // `synthesisId` (optional) is the id of the `syntheses` row the
  // frontend created BEFORE calling this — the backend writes the
  // streamed output back into that exact row so the new card the user
  // is staring at fills up live. Older callers that don't pass an id
  // still work (legacy single-card path).
  const generateProblem = useCallback(async (synthesisId?: string) => {
    store.setLastAction({ type: 'generateProblem', args: synthesisId ? { synthesisId } : undefined })
    streaming.reset()
    await sse.connect(
      `${API_URL}/api/projects/${projectId}/generate-problem`,
      synthesisId ? { synthesisId } : {},
    )
  }, [projectId, sse, store, streaming])
  
  // ---- Revise problem statement ----
  const reviseProblem = useCallback(async (revision: string, synthesisId?: string, currentRevision?: number) => {
    store.setLastAction({ type: 'reviseProblem', args: { revision, synthesisId, currentRevision } })
    const currentVersion = currentRevision ?? store.problemOutput?.version ?? 1
    streaming.reset()
    
    await sse.connect(`${API_URL}/api/projects/${projectId}/revise-problem`, {
      revision,
      currentVersion,
      ...(synthesisId ? { synthesisId } : {}),
    })
  }, [projectId, sse, store, streaming])
  
  // ---- Terminate branch ----
  const terminateBranch = useCallback(async (branchId: string, reason?: string) => {
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/branches/${branchId}/terminate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ reason }),
      }
    )
    
    if (!response.ok) throw new Error('Failed to terminate branch')
    const data = await response.json()
    store.updateBranch(branchId, data.branch)
  }, [projectId, headers, store])
  
  // ---- Prune branch (Round model v2: soft delete + cascade option) ----
  const pruneBranch = useCallback(async (branchId: string, cascade = false) => {
    const qs = cascade ? '?cascade=true' : '?cascade=false'
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/branches/${branchId}${qs}`,
      { method: 'DELETE', headers }
    )

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.detail || 'Failed to prune branch')
    }
    // Soft delete: keep the branch in the list but flip its status.
    store.pruneBranch(branchId)
    return await response.json()
  }, [projectId, headers, store])

  // ---- Terminate branch with frontend gating ----
  // Refuses if the active branch still has unanswered/unskipped questions
  // in the current round; mirrors the backend gate so the user gets an
  // immediate response instead of a 409 round-trip.
  const terminateBranchGated = useCallback(
    async (branchId: string, reason?: string) => {
      const readiness = store.getRoundReadiness(branchId)
      if (readiness === 'incomplete') {
        toast.error(
          'Answer or skip every question in this round before terminating the branch.',
        )
        return null
      }
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/branches/${branchId}/terminate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ reason }),
        },
      )
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to terminate branch')
      }
      const data = await response.json()
      store.updateBranch(branchId, data.branch)
      return data
    },
    [projectId, headers, store],
  )

  // ---- Save / clear a single answer draft (Round model v2) ----
  const saveDraft = useCallback(
    async (
      branchId: string,
      questionId: string,
      payload: {
        draftText: string
        draftStatus: Answer['status']
        selectedOptions?: string[]
        otherText?: string
      },
    ) => {
      try {
        await api.drafts.upsert(projectId, branchId, questionId, payload)
      } catch (err) {
        console.warn('Draft save failed', err)
      }
    },
    [projectId],
  )

  const clearDraft = useCallback(
    async (branchId: string, questionId: string) => {
      try {
        await api.drafts.remove(projectId, branchId, questionId)
      } catch (err) {
        console.warn('Draft clear failed', err)
      }
    },
    [projectId],
  )

  const hydrateDrafts = useCallback(async () => {
    const result = await api.drafts.listForRound(projectId)
    if (result.success && result.data) {
      store.hydrateDrafts(
        (result.data.drafts ?? []).map((d) => ({
          branchId: d.branchId,
          questionId: d.questionId,
          draftText: d.draftText,
          draftStatus: d.draftStatus,
          selectedOptions: d.selectedOptions,
          otherText: d.otherText,
        })),
      )
    }
  }, [projectId, store])

  // ---- Submit the global round (Round model v2) ----
  // Collects every active branch's in-memory answers and posts them in
  // one synchronized batch. The backend processes them, runs QAI for the
  // next round, and emits SSE events that update the UI in place.
  const submitRound = useCallback(async () => {
    if (!store.canSubmitRound()) {
      toast.error('Every active branch must have all questions answered or skipped.')
      return
    }
    const active = store.getActiveBranches()
    const payload = {
      branches: active.map((b) => {
        const answersMap = store.getAnswersForBranch(b._id)
        const answers: Answer[] = Array.from(answersMap.values())
        return { branchId: b._id, answers }
      }),
    }
    store.setLastAction({ type: 'submitRound' })
    store.setRoundSubmitting(true)
    try {
      await sse.connect(`${API_URL}/api/projects/${projectId}/round/submit`, payload)
    } catch (err) {
      store.setRoundSubmitting(false)
      throw err
    }
  }, [projectId, sse, store])
  
  // ---- Rename branch ----
  const renameBranch = useCallback(async (branchId: string, name: string) => {
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/branches/${branchId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ name }),
      }
    )
    
    if (!response.ok) throw new Error('Failed to rename branch')
    const data = await response.json()
    store.updateBranch(branchId, data.branch)
  }, [projectId, headers, store])
  
  // ---- Execute backend command ----
  const executeCommand = useCallback(async (command: string) => {
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/command`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ command }),
      }
    )
    
    if (!response.ok) throw new Error('Command failed')
    return await response.json()
  }, [projectId, headers])

  // ---- Continue-depth (ask OAI a follow-up question on the synthesis) ----
  const continueDepth = useCallback(async (query: string, synthesisId?: string) => {
    streaming.reset()
    await sse.connect(
      `${API_URL}/api/projects/${projectId}/continue-depth`,
      { query, ...(synthesisId ? { synthesisId } : {}) },
    )
  }, [projectId, sse, streaming])

  // ---- Free-text chat (non-slash command bar input) ----
  const freeChat = useCallback(async (message: string, branchId: string) => {
    if (!message.trim() || !branchId) return
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/branches/${branchId}/chat`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ message }),
      },
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.detail || 'Free chat failed')
    }
    return await response.json()
  }, [projectId, headers])

  // ---- Restore a previous problem-output version ----
  // Convex mutation is invoked via the React `useMutation` in WorkspacePage,
  // not exposed from this hook to keep concerns separated.

  // ---- Confirm reset ----
  const confirmReset = useCallback(async () => {
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/command/confirm-reset`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
      }
    )
    
    if (!response.ok) throw new Error('Reset failed')
    store.reset()
    return await response.json()
  }, [projectId, headers, store])

  // ---- Continue conversation (use backend response to switch UI) ----
  const continueConversation = useCallback(async () => {
    const result = await executeCommand('continue')
    if (!result?.data) return result

    const { state: targetState, branchId } = result.data as {
      state?: string
      branchId?: string
    }

    if (branchId) {
      store.setActiveBranch(branchId)
    }

    if (targetState === 'ANSWERING') {
      store.setState('ANSWERING')
    } else if (targetState === 'QUESTIONING') {
      store.setState('QUESTIONING')
    } else if (targetState === 'READY_TO_GENERATE') {
      store.setState('IDLE')
    }

    store.clearError()
    return result
  }, [executeCommand, store])

  // ---- Retry last action ----
  const retryLastAction = useCallback(async () => {
    const action = store.lastAction
    if (!action) return null

    store.clearError()

    switch (action.type) {
      case 'submitSituation':
        await submitSituation(action.args?.situation as string)
        break
      case 'confirmSituation':
        await confirmSituation(action.args?.useOriginal as boolean)
        break
      case 'submitAnswers':
        await submitAnswers(
          action.args?.branchId as string,
          action.args?.answers as unknown as Answer[],
        )
        break
      case 'requestAAIHelp':
        await requestAAIHelp(
          action.args?.questionId as string,
          action.args?.branchId as string,
          action.args?.context as string | undefined,
        )
        break
      case 'generateProblem':
        await generateProblem(action.args?.synthesisId as string | undefined)
        break
      case 'reviseProblem':
        await reviseProblem(
          action.args?.revision as string,
          action.args?.synthesisId as string | undefined,
          action.args?.currentRevision as number | undefined,
        )
        break
      default:
        return null
    }

    return { success: true }
  }, [store, submitSituation, confirmSituation, submitAnswers, requestAAIHelp, generateProblem, reviseProblem])
  
  // Disconnect SSE
  const disconnect = useCallback(() => {
    sse.disconnect()
  }, [sse])
  
  return {
    // State
    state: store.state,
    isStreaming: store.isStreaming,
    streamContent: store.streamContent,
    error: store.error,
    lastAction: store.lastAction,
    
    // Situation
    rawSituation: store.rawSituation,
    cleanedSituation: store.cleanedSituation,
    diff: store.diff,
    
    // Branches
    branches: store.branches,
    activeBranchId: store.activeBranchId,
    
    // Questions/Answers
    getQuestionsForBranch: store.getQuestionsForBranch,
    getAnswersForBranch: store.getAnswersForBranch,
    
    // AAI
    aaiState: store.aaiState,
    aaiSuggestion: store.aaiSuggestion,
    
    // Problem output
    problemOutput: store.problemOutput,
    
    // Actions
    submitSituation,
    confirmSituation,
    submitAnswers,
    requestAAIHelp,
    cancelAAI,
    dismissAAI,
    generateProblem,
    reviseProblem,
    terminateBranch,
    terminateBranchGated,
    pruneBranch,
    renameBranch,
    executeCommand,
    confirmReset,
    continueConversation,
    continueDepth,
    freeChat,
    retryLastAction,
    // Round model v2
    submitRound,
    saveDraft,
    clearDraft,
    hydrateDrafts,
    
    // State management
    setState: store.setState,
    setActiveBranch: store.setActiveBranch,
    clearError: store.clearError,
    reset: store.reset,
    disconnect,
    
    // SSE state
    sseConnected: sse.isConnected,
    sseError: sse.error,
  }
}

export default useConversation
